import { supabase } from './supabase';
import * as XLSX from 'xlsx';
import type { GuestListItem, GuestListUpload, GuestSearchResult } from '../types/tables';

export async function uploadGuestList(
  registryId: string,
  guestList: GuestListUpload[]
): Promise<{ success: boolean; message: string; count: number }> {
  try {
    // Validate input
    if (!registryId) {
      throw new Error('Registry ID is required');
    }

    if (!guestList.length) {
      throw new Error('No guests to upload');
    }

    // Format data for insertion
    const formattedData = guestList.map(guest => ({
      registry_id: registryId,
      guest_name: guest.guest_name.trim(),
      contact_info: guest.contact_info?.trim() || null,
      dietary_restrictions: guest.dietary_restrictions?.trim() || null,
      plus_one_status: guest.plus_one_status || false,
      plus_one_name: guest.plus_one_name?.trim() || null
    }));

    // Upsert guest list (update existing, insert new)
    const { error: upsertError } = await supabase
      .from('guest_tables')
      .upsert(formattedData, { 
        onConflict: 'registry_id,guest_name',
        ignoreDuplicates: false 
      });

    if (upsertError) throw upsertError;

    return {
      success: true,
      message: `Successfully uploaded ${formattedData.length} guests`,
      count: formattedData.length
    };
  } catch (error) {
    console.error('Error uploading guest list:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to upload guest list',
      count: 0
    };
  }
}

export async function getGuestList(registryId: string): Promise<GuestListItem[]> {
  try {
    const { data, error } = await supabase
      .from('guest_tables')
      .select('*')
      .eq('registry_id', registryId)
      .order('guest_name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting guest list:', error);
    return [];
  }
}

export async function addGuest(
  registryId: string,
  guest: { guest_name: string }
): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('guest_tables')
      .insert({
        registry_id: registryId,
        guest_name: guest.guest_name.trim(),
        table_number: 0
      });

    if (error) throw error;

    return {
      success: true,
      message: 'Guest added successfully'
    };
  } catch (error) {
    console.error('Error adding guest:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to add guest'
    };
  }
}

export async function updateGuest(
  guestId: string,
  updates: Partial<Omit<GuestListItem, 'id' | 'registry_id' | 'created_at' | 'updated_at'>>
): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('guest_tables')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', guestId);

    if (error) throw error;

    return {
      success: true,
      message: 'Guest updated successfully'
    };
  } catch (error) {
    console.error('Error updating guest:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update guest'
    };
  }
}

export async function deleteGuest(guestId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('guest_tables')
      .delete()
      .eq('id', guestId);

    if (error) throw error;

    return {
      success: true,
      message: 'Guest removed successfully'
    };
  } catch (error) {
    console.error('Error deleting guest:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to remove guest'
    };
  }
}

export async function searchGuestList(
  registryId: string,
  searchTerm: string
): Promise<GuestSearchResult[]> {
  try {
    if (!searchTerm.trim()) return [];

    const { data, error } = await supabase
      .from('guest_tables')
      .select('guest_name, contact_info, registry_id')
      .eq('registry_id', registryId)
      .ilike('guest_name', `%${searchTerm}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error searching guest list:', error);
    return [];
  }
}

export function parseExcelFile(file: File): Promise<GuestListUpload[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validate and transform data
        const guestList: GuestListUpload[] = [];
        
        for (const row of jsonData) {
          const entry = row as Record<string, any>;
          
          // Look for guest name column (case insensitive)
          const guestNameKey = Object.keys(entry).find(
            key => key.toLowerCase().includes('guest') || key.toLowerCase().includes('name')
          );
          
          // Look for contact info column
          const contactInfoKey = Object.keys(entry).find(
            key => key.toLowerCase().includes('contact') || key.toLowerCase().includes('email') || key.toLowerCase().includes('phone')
          );
          
          // Look for dietary restrictions column
          const dietaryKey = Object.keys(entry).find(
            key => key.toLowerCase().includes('dietary') || key.toLowerCase().includes('restriction') || key.toLowerCase().includes('allerg')
          );
          
          // Look for plus one status column
          const plusOneStatusKey = Object.keys(entry).find(
            key => key.toLowerCase().includes('plus') && key.toLowerCase().includes('one')
          );
          
          // Look for plus one name column
          const plusOneNameKey = Object.keys(entry).find(
            key => key.toLowerCase().includes('plus') && key.toLowerCase().includes('name')
          );
          
          if (!guestNameKey) {
            reject(new Error('Could not identify guest name column. Please ensure your file has a "Guest Name" or "Name" column.'));
            return;
          }
          
          const guestName = entry[guestNameKey];
          const contactInfo = contactInfoKey ? entry[contactInfoKey] : '';
          const dietaryRestrictions = dietaryKey ? entry[dietaryKey] : '';
          const plusOneStatus = plusOneStatusKey ? 
            (entry[plusOneStatusKey]?.toString().toLowerCase() === 'true' || 
             entry[plusOneStatusKey]?.toString().toLowerCase() === 'yes' || 
             entry[plusOneStatusKey] === 1) : false;
          const plusOneName = plusOneNameKey ? entry[plusOneNameKey] : '';
          
          if (!guestName) {
            continue; // Skip invalid rows
          }
          
          guestList.push({
            guest_name: String(guestName).trim(),
            contact_info: contactInfo ? String(contactInfo).trim() : undefined,
            dietary_restrictions: dietaryRestrictions ? String(dietaryRestrictions).trim() : undefined,
            plus_one_status: plusOneStatus,
            plus_one_name: plusOneName ? String(plusOneName).trim() : undefined
          });
        }
        
        if (guestList.length === 0) {
          reject(new Error('No valid guest names found in the file'));
          return;
        }
        
        resolve(guestList);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

export function parseCSVFile(file: File): Promise<GuestListUpload[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const lines = csvText.split('\n');
        
        if (lines.length < 2) {
          reject(new Error('CSV file must contain at least a header row and one data row'));
          return;
        }
        
        // Parse header row
        const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
        
        // Find column indexes
        const guestNameIndex = headers.findIndex(
          header => header.includes('guest') || header.includes('name')
        );
        
        const contactInfoIndex = headers.findIndex(
          header => header.includes('contact') || header.includes('email') || header.includes('phone')
        );
        
        const dietaryIndex = headers.findIndex(
          header => header.includes('dietary') || header.includes('restriction') || header.includes('allerg')
        );
        
        const plusOneStatusIndex = headers.findIndex(
          header => header.includes('plus') && header.includes('one')
        );
        
        const plusOneNameIndex = headers.findIndex(
          header => header.includes('plus') && header.includes('name')
        );
        
        if (guestNameIndex === -1) {
          reject(new Error('Could not identify guest name column. Please ensure your CSV has a "Guest Name" or "Name" column.'));
          return;
        }
        
        // Parse data rows
        const guestList: GuestListUpload[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          const values = line.split(',').map(value => value.trim());
          
          if (values.length <= guestNameIndex) {
            continue; // Skip invalid rows
          }
          
          const guestName = values[guestNameIndex];
          const contactInfo = contactInfoIndex !== -1 && values[contactInfoIndex] ? values[contactInfoIndex] : '';
          const dietaryRestrictions = dietaryIndex !== -1 && values[dietaryIndex] ? values[dietaryIndex] : '';
          const plusOneStatus = plusOneStatusIndex !== -1 && values[plusOneStatusIndex] ? 
            (values[plusOneStatusIndex].toLowerCase() === 'true' || 
             values[plusOneStatusIndex].toLowerCase() === 'yes' || 
             values[plusOneStatusIndex] === '1') : false;
          const plusOneName = plusOneNameIndex !== -1 && values[plusOneNameIndex] ? values[plusOneNameIndex] : '';
          
          if (!guestName) {
            continue; // Skip invalid rows
          }
          
          guestList.push({
            guest_name: guestName,
            contact_info: contactInfo || undefined,
            dietary_restrictions: dietaryRestrictions || undefined,
            plus_one_status: plusOneStatus,
            plus_one_name: plusOneName || undefined
          });
        }
        
        if (guestList.length === 0) {
          reject(new Error('No valid guest names found in the file'));
          return;
        }
        
        resolve(guestList);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}

export function generateSampleTemplate(): void {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Sample data with new guest information fields
  const sampleData = [
    { 
      'Guest Name': 'John Smith', 
      'Contact Info': 'john.smith@email.com', 
      'Dietary Restrictions': 'Vegetarian',
      'Plus One Status': 'Yes',
      'Plus One Name': 'Jane Smith'
    },
    { 
      'Guest Name': 'Jane Doe', 
      'Contact Info': '+94 77 123 4567', 
      'Dietary Restrictions': '',
      'Plus One Status': 'No',
      'Plus One Name': ''
    },
    { 
      'Guest Name': 'Robert Johnson', 
      'Contact Info': 'robert.j@email.com', 
      'Dietary Restrictions': 'Gluten-free',
      'Plus One Status': 'Yes',
      'Plus One Name': 'Sarah Johnson'
    },
    { 
      'Guest Name': 'Emily Williams', 
      'Contact Info': '+94 77 234 5678', 
      'Dietary Restrictions': 'No restrictions',
      'Plus One Status': 'No',
      'Plus One Name': ''
    },
    { 
      'Guest Name': 'Michael Brown', 
      'Contact Info': 'michael.brown@email.com', 
      'Dietary Restrictions': 'Vegan',
      'Plus One Status': 'Yes',
      'Plus One Name': 'Lisa Brown'
    }
  ];
  
  // Create a worksheet
  const worksheet = XLSX.utils.json_to_sheet(sampleData);
  
  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest List');
  
  // Generate Excel file
  XLSX.writeFile(workbook, 'guest_list_template.xlsx');
}